import { articles, allTags, categories } from '@/mocks/articles';
import type { Article, CategoryMeta, CategorySlug } from '@/types/article';

export { articles, allTags, categories };

export function getArticleById(id: string): Article | undefined {
  return articles.find((a) => a.id === id);
}

export function getCategoryBySlug(slug: string): CategoryMeta | undefined {
  return categories.find((c) => c.slug === slug);
}

export function getArticlesByCategory(slug: string): Article[] {
  return articles
    .filter((a) => a.categorySlug === slug)
    .sort((a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime());
}

export function getArticlesByTag(tag: string): Article[] {
  return articles.filter((a) => a.tags.includes(tag));
}

export function searchArticles(keyword: string): Article[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return articles.filter(
    (a) =>
      a.title.toLowerCase().includes(q) ||
      a.excerpt.toLowerCase().includes(q) ||
      a.tags.some((t) => t.toLowerCase().includes(q)) ||
      a.category.toLowerCase().includes(q),
  );
}

export function getLatestArticles(limit?: number): Article[] {
  const sorted = [...articles].sort(
    (a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime(),
  );
  return typeof limit === 'number' ? sorted.slice(0, limit) : sorted;
}

export function getHotArticles(limit?: number, days?: number): Article[] {
  let list = [...articles];
  if (typeof days === 'number') {
    const current = new Date('2026-10-15T10:00:00').getTime();
    const threshold = current - days * 24 * 60 * 60 * 1000;
    list = list.filter((a) => new Date(a.publishTime).getTime() >= threshold);
  }
  list.sort((a, b) => b.views - a.views);
  return typeof limit === 'number' ? list.slice(0, limit) : list;
}

export function getRelatedArticles(article: Article, limit = 3): Article[] {
  const sameCategory = articles.filter(
    (a) => a.id !== article.id && a.categorySlug === article.categorySlug,
  );
  const byTag = articles.filter(
    (a) =>
      a.id !== article.id &&
      a.categorySlug !== article.categorySlug &&
      a.tags.some((t) => article.tags.includes(t)),
  );
  const merged = [...sameCategory, ...byTag];
  const seen = new Set<string>();
  const result: Article[] = [];
  for (const item of merged) {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      result.push(item);
    }
    if (result.length >= limit) break;
  }
  return result;
}

export function getTopTags(limit?: number): { tag: string; count: number }[] {
  const map = new Map<string, number>();
  articles.forEach((a) => a.tags.forEach((t) => map.set(t, (map.get(t) || 0) + 1)));
  const list = Array.from(map.entries())
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count);
  return typeof limit === 'number' ? list.slice(0, limit) : list;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  const hh = `${d.getHours()}`.padStart(2, '0');
  const mm = `${d.getMinutes()}`.padStart(2, '0');
  return `${y}-${m}-${day} ${hh}:${mm}`;
}

export function relativeTime(iso: string): string {
  const now = new Date('2026-10-15T10:00:00').getTime();
  const target = new Date(iso).getTime();
  const diff = Math.max(0, now - target);
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes || 1}分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}天前`;
  return formatDate(iso).slice(0, 10);
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export function isCategorySlug(value: string): value is CategorySlug {
  return categories.some((c) => c.slug === value);
}